import PropTypes from 'prop-types';
import { Empty, Form, notification } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import RowControllerContact from './RowControllerContact';
import api from '../../../utils/appApi';
import getApiErrorMessages from '../../../utils/getApiErrorMessages';
import {
  CONTACT_MERGE_FIELDS,
  getContactDefaultSide,
  mergeContacts,
} from '../dataRowUtils';

const pickSide = (row, side) => (side === 'prev' ? row.prev : row.next);

// The base side, whose id/values are kept by default. A pad-level override
// (deleted / owned, from checkOverrides) wins when its side actually has a
// contact for this pair.
const resolveCheckedSide = (row, configOverride, defaultSide) => {
  const forced = configOverride?.checked;
  if (forced && pickSide(row, forced)) return forced;
  if (defaultSide && pickSide(row, defaultSide)) return defaultSide;
  return getContactDefaultSide(row.prev, row.next);
};

// Builds the editable state for a merge row: the "base" side (its id is kept),
// the current value of each field, and - for one-sided rows - whether the
// contact is included in the merged result.
function buildRowState(row, configOverride, defaultSide) {
  const checkedSide = resolveCheckedSide(row, configOverride, defaultSide);
  const base = pickSide(row, checkedSide);

  return {
    key: row.key,
    kind: row.kind,
    prev: row.prev,
    next: row.next,
    isOneSided: row.kind === 'prevOnly' || row.kind === 'nextOnly',
    checkedSide,
    included: true,
    values: CONTACT_MERGE_FIELDS.reduce(
      (acc, field) => ({ ...acc, [field]: base?.[field] ?? null }),
      {},
    ),
  };
}

export default function DataRowControllerContacts(props) {
  const {
    label,
    name,
    prev,
    next,
    setValue,
    contactTypeOptions,
    padId,
    configOverride,
    defaultSide,
    warnLessComplete,
  } = props;

  // The parent passes freshly filtered arrays on every render, so the merge is
  // keyed on the data content rather than array identity to avoid wiping edits
  // on unrelated re-renders. It only re-initialises when the source data changes.
  const contactsSignature = useMemo(() => {
    const sign = (list) =>
      (list || []).map((contact) =>
        CONTACT_MERGE_FIELDS.reduce(
          (acc, field) => ({ ...acc, [field]: contact?.[field] }),
          { id: contact?.id },
        ),
      );
    return JSON.stringify({ prev: sign(prev), next: sign(next) });
  }, [prev, next]);

  const merged = useMemo(
    () => mergeContacts(prev, next),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contactsSignature],
  );
  const [rows, setRows] = useState(() =>
    merged.map((row) => buildRowState(row, configOverride, defaultSide)),
  );

  useEffect(() => {
    setRows(
      merged.map((row) => buildRowState(row, configOverride, defaultSide)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merged]);

  useEffect(() => {
    const result = rows
      .filter((row) => row.included)
      .map((row) => {
        const base = pickSide(row, row.checkedSide);
        const merge = CONTACT_MERGE_FIELDS.reduce(
          (acc, field) => ({
            ...acc,
            [field]:
              field === 'is_primary' ? !!row.values[field] : row.values[field],
          }),
          {},
        );
        return base?.id != null ? { id: base.id, ...merge } : merge;
      });

    setValue(result);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const updateRow = useCallback(
    (key, updater) =>
      setRows((prevRows) =>
        prevRows.map((row) => (row.key === key ? updater(row) : row)),
      ),
    [],
  );

  // Take a whole side: its id becomes the base and every value follows it.
  const onPickWholeSide = useCallback(
    (key) => (side) =>
      updateRow(key, (row) => {
        const base = pickSide(row, side);
        if (!base) return row;
        return {
          ...row,
          checkedSide: side,
          values: CONTACT_MERGE_FIELDS.reduce(
            (acc, field) => ({ ...acc, [field]: base[field] ?? null }),
            {},
          ),
        };
      }),
    [updateRow],
  );

  // Override a single field's value from one side; the base id is untouched.
  const onPickField = useCallback(
    (key) => (field, side) =>
      updateRow(key, (row) => {
        const source = pickSide(row, side);
        if (!source) return row;
        return { ...row, values: { ...row.values, [field]: source[field] } };
      }),
    [updateRow],
  );

  const onFieldChange = useCallback(
    (key) => (field, value) =>
      updateRow(key, (row) => ({
        ...row,
        values: { ...row.values, [field]: value },
      })),
    [updateRow],
  );

  const onToggleInclude = useCallback(
    (key) => () =>
      updateRow(key, (row) => ({ ...row, included: !row.included })),
    [updateRow],
  );

  // Deletes the contact server-side from each pad it exists on, then drops the
  // row so it is removed from both the UI and the merged result.
  const onDeleteRow = useCallback(
    (row) => {
      const calls = [];

      if (row.prev?.id != null) {
        calls.push(
          api.pad.deleteOwner(
            padId.prev,
            row.prev.id,
            row.prev.contact_type_id,
          ),
        );
      }
      if (row.next?.id != null) {
        calls.push(
          api.pad.deleteOwner(
            padId.next,
            row.next.id,
            row.next.contact_type_id,
          ),
        );
      }

      return Promise.all(calls)
        .then(() =>
          setRows((prevRows) => prevRows.filter((r) => r.key !== row.key)),
        )
        .catch((err) => {
          notification.error({
            message: 'Could not delete contact',
            description: getApiErrorMessages(err),
          });
          return Promise.reject(err);
        });
    },
    [padId],
  );

  return (
    <div className="data-row-item contacts">
      <label htmlFor={name}>{label}</label>
      {rows.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No contacts" />
      ) : (
        rows.map((row) => (
          <RowControllerContact
            key={row.key}
            row={row}
            options={contactTypeOptions}
            onPickWholeSide={onPickWholeSide(row.key)}
            onPickField={onPickField(row.key)}
            onFieldChange={onFieldChange(row.key)}
            onToggleInclude={onToggleInclude(row.key)}
            onDelete={() => onDeleteRow(row)}
            warnLessComplete={warnLessComplete}
          />
        ))
      )}
      <Form.Item name={name} noStyle />
    </div>
  );
}

DataRowControllerContacts.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  prev: PropTypes.array,
  next: PropTypes.array,
  setValue: PropTypes.func,
  contactTypeOptions: PropTypes.array,
  padId: PropTypes.shape({
    prev: PropTypes.number,
    next: PropTypes.number,
  }),
  configOverride: PropTypes.shape({
    checked: PropTypes.oneOf(['prev', 'next', null]),
    conflicts: PropTypes.bool,
  }),
  defaultSide: PropTypes.oneOf(['prev', 'next']),
  warnLessComplete: PropTypes.bool,
};

DataRowControllerContacts.defaultProps = {
  label: '',
  prev: [],
  next: [],
  setValue: () => null,
  contactTypeOptions: [],
  padId: {},
  configOverride: null,
  defaultSide: null,
  warnLessComplete: false,
};
